<template>
  <div class="musubi-page musubi-page--cnreview">
    <header class="musubi-page__head">
      <h1>Canal Nav review</h1>
      <p class="text-muted">
        Canalside businesses the directory build could not auto-classify — usually a
        generic pub or bar name that matched Companies House too weakly, so the entity
        type is unknown and the GDPR gate held the record back. Confirm the real type
        (or exclude it) to release each one to the Canal Nav export.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <div v-if="records.length === 0" class="cnr-empty">
        <CygnusIcon name="check-circle" class="me-2" />Nothing to review — the queue is clear.
      </div>

      <table v-else class="cnr-table">
        <thead>
          <tr>
            <th>Business</th>
            <th>Category</th>
            <th>Water</th>
            <th>Why held</th>
            <th class="cnr-actions-col">Verdict</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in records" :key="r.prospect_id" :class="{ 'is-resolving': busyId === r.prospect_id }">
            <td>
              <div class="cnr-name">{{ r.name || '(unnamed)' }}</div>
              <a v-if="r.website" :href="r.website" target="_blank" rel="noopener noreferrer" class="cnr-web">
                {{ r.domain || r.website }}
              </a>
              <span v-else class="cnr-nolink">no website</span>
              <span v-if="r.town" class="cnr-town"> · {{ r.town }}</span>
            </td>
            <td><span class="cnr-cat">{{ prettyCat(r.category) }}</span></td>
            <td>
              <span v-if="r.distance_to_water_m != null" class="cnr-dist">{{ r.distance_to_water_m }} m</span>
              <span v-else class="cnr-dist cnr-dist--none">—</span>
            </td>
            <td>
              <span class="cnr-reason">{{ prettyReason(r) }}</span>
            </td>
            <td class="cnr-actions">
              <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busyId === r.prospect_id"
                      @click="act(r, 'confirm_corporate')">Corporate</button>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="busyId === r.prospect_id"
                      @click="act(r, 'confirm_sole_trader')">Sole trader</button>
              <button type="button" class="btn btn-outline-danger btn-sm" :disabled="busyId === r.prospect_id"
                      @click="act(r, 'exclude')">Exclude</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="lastResult" :class="['cnr-toast', lastResult.ok ? 'is-ok' : 'is-error']">{{ lastResult.message }}</div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/canal-nav/review';

const READINESS_LABEL = {
  ready: 'released to export (ready)',
  ineligible_contact_but_exportable: 'exported as directory data (no drip email)',
  excluded: 'excluded from export',
  needs_review: 'still needs review',
};

export default {
  name: 'AdminCanalNavReview',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      error: null,
      records: [],
      busyId: null,
      lastResult: null,
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.records = Array.isArray(p?.records) ? p.records : [];
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load the review queue.';
      } finally {
        this.loading = false;
      }
    },
    async act(row, action) {
      this.busyId = row.prospect_id;
      this.lastResult = null;
      try {
        const res = await this.api.post(`${ENDPOINT}/${row.prospect_id}/action`, { action });
        const p = res?.data ?? res;
        if (p?.success) {
          // Drop the resolved row from the queue and report the outcome.
          this.records = this.records.filter((r) => r.prospect_id !== row.prospect_id);
          const label = READINESS_LABEL[p.readiness] || p.readiness || 'updated';
          this.lastResult = { ok: true, message: `${row.name || 'Record'} — ${label}.` };
        } else {
          this.lastResult = { ok: false, message: p?.error || 'Action failed.' };
        }
      } catch (e) {
        this.lastResult = { ok: false, message: e?.response?.data?.error || e?.message || 'Action failed.' };
      } finally {
        this.busyId = null;
      }
    },
    prettyCat(cat) {
      if (!cat) return 'other';
      return String(cat).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },
    prettyReason(r) {
      const bits = [];
      if (r.registration_status) bits.push(`CH: ${r.registration_status}`);
      if (r.match_confidence != null) bits.push(`match ${r.match_confidence}%`);
      if (bits.length) return bits.join(' · ');
      const signal = { weak_or_no_match: 'weak / no CH match', ch_not_run: 'CH not run' };
      return signal[r.reason] || r.reason || 'unclassified';
    },
  },
};
</script>

<style scoped>
.musubi-page--cnreview { padding: var(--m-space-6, 32px); max-width: 1000px; }
.musubi-page__head p { max-width: 720px; }

.cnr-empty { display: flex; align-items: center; font-size: 0.9rem; color: var(--m-text-secondary); margin-top: 20px; }

.cnr-table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 0.86rem; }
.cnr-table th { text-align: left; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 600; padding: 6px 12px 6px 0; border-bottom: 1px solid var(--m-border, #e5e7eb); }
.cnr-table td { padding: 12px 12px 12px 0; border-bottom: 1px solid var(--m-border-subtle, #f1f2f4); vertical-align: top; }
.cnr-table tr.is-resolving { opacity: 0.5; }

.cnr-name { font-weight: 600; color: var(--m-text-primary); }
.cnr-web { font-size: 0.78rem; color: var(--m-brand, #2563eb); text-decoration: none; }
.cnr-web:hover { text-decoration: underline; }
.cnr-nolink { font-size: 0.78rem; color: var(--m-text-tertiary); }
.cnr-town { font-size: 0.78rem; color: var(--m-text-secondary); }

.cnr-cat { font-size: 0.8rem; color: var(--m-text-secondary); }
.cnr-dist { font-size: 0.82rem; color: var(--m-text-primary); }
.cnr-dist--none { color: var(--m-text-tertiary); }
.cnr-reason { font-size: 0.78rem; color: var(--m-text-secondary); }

.cnr-actions-col { width: 260px; }
.cnr-actions { display: flex; gap: 6px; flex-wrap: wrap; }

.cnr-toast { margin-top: 16px; font-size: 0.85rem; }
.cnr-toast.is-ok { color: var(--m-status-success, #10B981); }
.cnr-toast.is-error { color: var(--m-status-danger, #EF4444); }
</style>
